import { AvailabilityRange, AvailabilityRestrictionEnum } from "@clockwise/schema/v2";
import { useGatewayMutation } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { SchedulingLinkCard } from "@clockwise/web-commons/src/ui/scheduling-link-card";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import {
  getEditSchedulingLinkPath,
  getSchedulingLinkPath,
} from "@clockwise/web-commons/src/util/scheduling.util";
import React, { useCallback } from "react";
import copyToClipboard from "../../util/copyToClipboard";
import { FindOrCreateLinkNameDocument } from "./__generated__/FindOrCreateLinkName.v2.generated";

export type SidebarLink = {
  id: string;
  url: string;
  linkName: string | null;
  schedulingLink: {
    id: string;
    name: string;
    slug: string;
    durations: number[];
    defaultDuration: number;
    availabilityRange: AvailabilityRange | null;
    active: boolean;
    availabilityRestriction: AvailabilityRestrictionEnum;
    linkMembers: Array<{
      id: string;
      member: {
        id: string;
        person: {
          id: string;
          givenName?: string | null;
          familyName?: string | null;
          externalImageUrl?: string | null;
        };
      };
    }>;
    singleUse: boolean;
  };
};

export const DEFAULT_LINK_ID = "DEFAULT_LINK";
export const DEFAULT_LINK: SidebarLink = {
  id: DEFAULT_LINK_ID,
  url: "",
  linkName: null,
  schedulingLink: {
    id: "",
    durations: [30],
    defaultDuration: 30,
    availabilityRange: {
      __typename: "RollingWindow",
      windowSize: 14,
    },
    active: true,
    name: "Quick Meeting",
    slug: "quick-meeting",
    availabilityRestriction: AvailabilityRestrictionEnum.BalanceAvailabilityAndFocus,
    linkMembers: [
      {
        id: "fake@fake.com",
        member: {
          id: "",
          person: {
            id: "",
            givenName: "",
            familyName: "",
            externalImageUrl: null,
          },
        },
      },
    ],
    singleUse: false,
  },
};

export const isDefaultLink = (link: { id: string }) => link.id === DEFAULT_LINK_ID;
export function findInitialDefaultLink<T extends { schedulingLink: { name: string } }>(
  links: Array<T> | null | undefined,
) {
  // Find the newly created default link in the returned data.
  return links?.find((l) => l.schedulingLink.name === DEFAULT_LINK.schedulingLink.name);
}

interface Props {
  links: SidebarLink[];
  title?: string;
  onOpenUrl: (url: string) => void;
  onChangeModalLink: (link: SidebarLink) => void | Promise<void>;
  surface: "sidebar" | "mini";
}

export function LinksSection({ links, title, onOpenUrl, onChangeModalLink, surface }: Props) {
  const track = useTracking();
  const [findOrCreateDefaultLinkName] = useGatewayMutation(FindOrCreateLinkNameDocument);
  // Create the user's actual "Quick Meeting" link, and then return it for use by the various handlers below.
  const createRealQuickMeeting = useCallback(async () => {
    const { data } = await findOrCreateDefaultLinkName();
    const linkName = data?.createDefaultLinkName?.linkName;
    const initialLink = findInitialDefaultLink(data?.createDefaultLinkName?.schedulingLinks);
    return [linkName, initialLink] as [string | null | undefined, typeof initialLink];
  }, [findOrCreateDefaultLinkName]);

  const getOrCreateLink = async (
    linkName: string | null | undefined,
    link: SidebarLink,
  ): Promise<[string | null | undefined, SidebarLink | undefined]> => {
    if (!linkName || isDefaultLink(link)) {
      return await createRealQuickMeeting();
    }
    return [linkName, link];
  };

  const createHandler = (
    callback: (linkName: string, link: SidebarLink) => void,
    trackingEvent: keyof typeof TrackingEvents.LINKS.LINK,
  ) => (link: SidebarLink) => async () => {
    const [realLinkName, realLink] = await getOrCreateLink(link.linkName, link);
    if (realLinkName && realLink) {
      callback(realLinkName, realLink);
      track(TrackingEvents.LINKS.LINK[trackingEvent], {
        surface,
        meetingSettingsId: realLink.schedulingLink.id,
      });
    }
  };

  const onOpenLink = createHandler(
    (linkName, link) => onOpenUrl(getSchedulingLinkPath(linkName, link.schedulingLink.slug)),
    "VIEW_PREVIEW",
  );

  const onEditLink = createHandler(
    (linkName, link) => onOpenUrl(getEditSchedulingLinkPath(linkName, link.schedulingLink.slug)),
    "EDIT_LINK",
  );

  const onCopyLink = createHandler((_linkName, link) => copyToClipboard(link.url), "COPY_LINK");

  const onOpenShareModal = createHandler(
    (_linkName, link) => void onChangeModalLink(link),
    "COPY_TIMES",
  );

  return (
    <div className="cw-space-y-2">
      {title && <h3 className="cw-heading-lg cw-space-y-3">{title}</h3>}
      {links.map((link) => {
        return (
          <SchedulingLinkCard
            key={link.id || "fake_quick_meeting" /* The only possibility when id is undefined */}
            name={link.schedulingLink.name}
            availabilityType={link.schedulingLink.availabilityRestriction}
            durations={link.schedulingLink.durations}
            defaultDuration={link.schedulingLink.defaultDuration}
            active={link.schedulingLink.active}
            linkMembers={link.schedulingLink.linkMembers.map((lm) => ({
              calendarId: lm.id,
              userProfile: lm.member.person as Required<typeof lm.member.person>,
            }))}
            onEditLink={onEditLink(link)}
            onCopyTimes={onOpenShareModal(link)}
            onOpen={onOpenLink(link)}
            onCopyLink={onCopyLink(link)}
          />
        );
      })}
    </div>
  );
}
